import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Minus, Plus, Check, Truck, ShieldCheck, Leaf, ArrowLeft, PlayCircle, RefreshCw } from "lucide-react";
import { getProduct as mockGetProduct } from "../mock";
import { DEFAULT_VIDEO_ID } from "../mock";
import ProductCard from "../components/ProductCard";
import VideoModal from "../components/VideoModal";
import { TrustBadges, ImportantInfo, ProductReviews } from "../components/ProductExtras";
import { useCart } from "../context/CartContext";
import { useStore } from "../context/StoreContext";
import { useSeo, productJsonLd } from "../hooks/useSeo";

const ProductDetail = () => {
  const { slug } = useParams();
  const { getProduct, products } = useStore();
  const product = getProduct(slug) || mockGetProduct(slug);
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [showVideo, setShowVideo] = useState(false);
  useSeo(product
    ? { title: `${product.name}`, description: `${product.description} Made in the UK. £${Number(product.price).toFixed(2)}.`, image: product.image, jsonLd: productJsonLd(product) }
    : { title: "Product not found", noindex: true });

  if (!product) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-display text-3xl font-bold uppercase text-[#1B4332]">Product not found</h1>
        <Link to="/shop" className="mt-6 inline-block rounded-full bg-[#2D6A4F] px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white">Back to Shop</Link>
      </div>
    );
  }

  const onSale = product.compareAt && product.compareAt > product.price;
  const related = products.filter((p) => p.id !== product.id && p.categories.some((c) => product.categories.includes(c))).slice(0, 4);

  return (
    <div>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <Link to="/shop" className="mb-6 inline-flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-wider text-[#2D6A4F] hover:text-[#1B4332]">
          <ArrowLeft size={15} /> Back to Shop
        </Link>

        <div className="grid gap-10 lg:grid-cols-2">
          {/* Image */}
          <div className="relative overflow-hidden rounded-3xl border border-[#e6f0ea] bg-white">
            <img src={product.image} alt={product.name} className="aspect-square w-full object-contain p-8" />
            {product.badge && (
              <span className={`absolute left-5 top-5 rounded-full px-4 py-1.5 font-display text-xs font-bold uppercase tracking-wider text-white ${product.badge === "Sale" ? "bg-[#c1121f]" : "bg-[#1B4332]"}`}>{product.badge}</span>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="mb-3 flex items-center gap-2">
              <div className="flex text-[#2D6A4F]">{[0,1,2,3,4].map(i => <Star key={i} size={16} className="fill-current" />)}</div>
              <span className="text-sm text-[#6b7d72]">{product.rating} · {product.reviews} reviews</span>
            </div>
            <h1 className="font-display text-3xl font-bold uppercase leading-tight text-[#1B2a21] sm:text-4xl">{product.name}</h1>

            <div className="mt-4 flex items-baseline gap-3">
              <span className="font-display text-3xl font-bold text-[#1B4332]">£{product.price.toFixed(2)}</span>
              {onSale && <span className="text-lg text-[#a2b3a9] line-through">£{product.compareAt.toFixed(2)}</span>}
              {onSale && <span className="rounded-full bg-[#fbe9e9] px-3 py-1 font-display text-xs font-bold uppercase text-[#c1121f]">Save £{(product.compareAt - product.price).toFixed(2)}</span>}
            </div>

            <TrustBadges product={product} />

            <p className="mt-5 leading-relaxed text-[#4a5c51]">{product.description}</p>

            <button
              onClick={() => setShowVideo(true)}
              data-testid="product-video-button"
              className="group mt-5 flex w-full items-center gap-3 rounded-2xl border border-[#cfe3d6] bg-[#f0f7f2] px-5 py-4 text-left transition hover:border-[#52B788] hover:bg-[#e6f3ec]"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#D62828] text-white transition group-hover:bg-[#A4161A]">
                <PlayCircle size={22} />
              </span>
              <span>
                <span className="block font-display text-sm font-bold uppercase tracking-wide text-[#D62828]">
                  What is it &amp; its benefits?
                </span>
                <span className="block text-sm text-[#D62828]">Watch a quick video explainer</span>
              </span>
            </button>

            {product.bundleItems && (
              <div className="mt-5 rounded-2xl bg-[#f0f7f2] p-4">
                <p className="mb-2 font-display text-xs font-semibold uppercase tracking-wider text-[#2D6A4F]">This bundle includes</p>
                <ul className="space-y-1">
                  {product.bundleItems.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-sm text-[#34463c]"><Check size={15} className="text-[#2D6A4F]" /> {b}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 flex items-center gap-4">
              <div className="flex items-center gap-3 rounded-full border border-[#dceadf] px-2 py-1">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-9 w-9 items-center justify-center rounded-full text-[#2D6A4F] hover:bg-[#f0f7f2]"><Minus size={16} /></button>
                <span className="w-6 text-center font-display font-bold">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="flex h-9 w-9 items-center justify-center rounded-full text-[#2D6A4F] hover:bg-[#f0f7f2]"><Plus size={16} /></button>
              </div>
              <button
                onClick={() => addItem(product, qty)}
                className="flex-1 rounded-full bg-[#2D6A4F] py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-[#1B4332] active:scale-[0.99]"
              >
                Add To Basket
              </button>
            </div>

            <div className="mt-4">
              <p className="mb-2 flex items-center gap-1.5 text-sm text-[#2D6A4F]">
                <RefreshCw size={15} />
                <span><strong className="font-semibold">Subscribe &amp; save</strong> — each month your subscription will automatically be delivered</span>
              </p>
              <button
                onClick={() => addItem({ ...product, id: `${product.id}-sub`, short: `${product.short || product.name} (Monthly)` }, qty)}
                className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#2D6A4F] bg-white py-3.5 font-display text-sm font-bold uppercase tracking-wider text-[#2D6A4F] transition hover:bg-[#f0f7f2] active:scale-[0.99]"
              >
                <RefreshCw size={16} /> Buy / Subscribe
              </button>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { icon: Leaf, t: "Nasty Free" },
                { icon: ShieldCheck, t: "UK Made" },
                { icon: Truck, t: "Free Over £40" },
              ].map((f, i) => (
                <div key={i} className="flex flex-col items-center gap-1 rounded-2xl bg-[#f0f7f2] py-4 text-center">
                  <f.icon size={20} className="text-[#2D6A4F]" />
                  <span className="font-display text-[11px] font-semibold uppercase tracking-wide text-[#34463c]">{f.t}</span>
                </div>
              ))}
            </div>

            <ImportantInfo product={product} />
          </div>
        </div>
      </div>

      <ProductReviews product={product} />

      {related.length > 0 && (
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <h2 className="mb-8 font-display text-3xl font-bold uppercase text-[#1B2a21]">You May Also Like</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}

      <VideoModal
        open={showVideo}
        onClose={() => setShowVideo(false)}
        videoId={product.videoId || DEFAULT_VIDEO_ID}
        title={`${product.short || product.name} \u2014 Benefits`}
      />
    </div>
  );
};

export default ProductDetail;
